import { LoadingButton } from "@mui/lab";
import { Box, Button, Grid, Icon, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import ReactQuillEditor from "app/components/editor/ReactQuillEditor";
import { Span } from "app/components/Typography";
import { useGenerateJd } from "app/hooks/ai/useGenerateJd";
import { useAddBenefits } from "app/hooks/benefit/useAddBenefits";
import { useGetBenefits } from "app/hooks/benefit/useGetBenefits";
import useSendTestEmailNotification from "app/hooks/job/useSendTestEmailNotification";
import { useAddSkills } from "app/hooks/skill/useAddSkills";
import useAuth from "app/hooks/useAuth";
import { convertListToString, getCurrentTimeStamp } from "app/utils/utils";
import dayjs from "dayjs";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import AddNewBenefitsDialog from "./AddNewBenefitsDialog";
import AddNewSkillDialog from "./AddNewSkillDialog";
import ContentBox from "./ContentBox";
import AddBenefits from "./Post Job Form/AddBenefits";
import DatesAndExperience from "./Post Job Form/DatesAndExperience";
import AddKeywords from "./Post Job Form/AddKeywords";
import AddQualificationRequirement from "./Post Job Form/AddQualificationRequirement";
import AddTitleAndCompany from "./Post Job Form/AddTitleAndCompany";
import ConfirmOverwriteDialog from "./Post Job Form/ConfirmOverwriteDialog";
import MissingJDFieldsDialog from "./Post Job Form/MissingJDFieldsDialog";
import MultiSelectLocation from "./Post Job Form/MultiSelectLocation";
import SkillsAndSalary from "./Post Job Form/SkillsAndSalary";

const dateToday = dayjs(new Date());
const dateOneMonthAfter = dateToday.add(1, "month");
const filterOptions = (options, state) => {
  return options.filter((option) =>
    option.label.toLowerCase().includes(state.inputValue.toLowerCase())
  );
};

const isJobDescriptionEmpty = (description = "") =>
  String(description || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length === 0;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
`;

function GenerateJobDescriptionButton({
  label = "Generate With AI",
  disabled = false,
  loading,
  onGenerate
}) {
  return (
    <Button type="button" variant="contained" disabled={disabled || loading} onClick={onGenerate}>
      {loading ? (
        <Typography
          component="span"
          sx={{
            color: "inherit",
            fontSize: "inherit",
            fontWeight: "inherit",
            animation: `${pulse} 1.5s ease-in-out infinite`
          }}
        >
          Generating...
        </Typography>
      ) : (
        label
      )}
    </Button>
  );
}

function SubmitJobButton({ disabled, isEdit, loading }) {
  return (
    <LoadingButton
      disabled={disabled}
      color="primary"
      variant="contained"
      type="submit"
      sx={{ width: { xs: "100%", md: "inherit" } }}
      loading={loading}
    >
      <Icon>send</Icon>
      <Span sx={{ pl: 1, textTransform: "capitalize" }}>{isEdit ? "Update Job" : "Post Job"}</Span>
    </LoadingButton>
  );
}

function SendTestEmailButton({ disabled, emailSent, loading, onSend }) {
  return (
    <LoadingButton
      type="button"
      variant="outlined"
      onClick={onSend}
      loading={loading}
      disabled={disabled}
    >
      <Icon>mail</Icon>
      <Span sx={{ pl: 1, textTransform: "capitalize" }}>
        {emailSent ? "Email Sent" : "Send Test Email Notification"}
      </Span>
    </LoadingButton>
  );
}

function PostJobForm({
  mutationFn,
  preloadedData,
  skills,
  title = "",
  loadingState,
  disableSubmit
}) {
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setError,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: {
      jp_start_date: preloadedData?.jp_start_date ? dayjs(preloadedData.jp_start_date) : dateToday,
      jp_end_date: preloadedData?.jp_end_date
        ? dayjs(preloadedData.jp_end_date)
        : dateOneMonthAfter,
      jp_state: preloadedData?.jp_state,
      jp_country: preloadedData?.jp_country,
      jp_city: preloadedData?.jp_city || undefined
    }
  });
  const { user } = useAuth();
  const isFieldError = Object.keys(errors).length > 0;

  const [hasGeneratedDescription, setHasGeneratedDescription] = useState(false);
  const [openSkillDialog, setOpenSkillDialog] = useState(false); // For add skill dialog
  const [openBenefitDialog, setOpenBenefitDialog] = useState(false); // For add benefit dialog
  const [jobDescription, setJobDescription] = useState(
    preloadedData ? preloadedData.jp_description : ""
  );
  const [descriptionError, setDescriptionError] = useState({ error: false, message: "" });
  const [editorVersion, setEditorVersion] = useState(0);
  const [emailSent, setEmailSent] = useState(false);
  const [disableEmailButton, setDisableEmailButton] = useState(false);

  const [showConfirmOverwriteDialog, setShowConfirmOverwriteDialog] = useState(false);
  const [showMissingJDFieldsDialog, setShowMissingJDFieldsDialog] = useState(false);
  const [missingJDFields, setMissingJDFields] = useState([]);
  const [showJDWarning, setShowJDWarning] = useState(false);

  const startDate = watch("jp_start_date");
  const endDate = watch("jp_end_date");
  const jp_min_exp = watch("jp_min_exp");
  const jp_max_exp = watch("jp_max_exp");
  const minimumSalary = watch("jp_minimum_salary");
  const maximumSalary = watch("jp_maximum_salary");

  const location = useLocation();
  const isEditPath = location.pathname.includes("/jobs/listings") ? true : false;
  const isAdminRole = user.role === "SA";
  const isEdit = title.includes("Edit") ? true : false;
  const isEmailVerified = preloadedData?.jp_is_email_verified || false;
  const showSendEmailButton = isEdit && !isEmailVerified && isAdminRole ? true : false;

  const validateExperience = useCallback(() => {
    const min = Number(jp_min_exp);
    const max = Number(jp_max_exp);

    // Clear previous errors first
    clearErrors(["jp_min_exp", "jp_max_exp"]);

    // Validate min experience
    if (!/^\d+(\.\d+)?$/.test(jp_min_exp)) {
      setError("jp_min_exp", {
        type: "manual",
        message: "Enter a valid number"
      });
      return;
    }

    // Validate max experience
    if (!/^\d+(\.\d+)?$/.test(jp_max_exp)) {
      setError("jp_max_exp", {
        type: "manual",
        message: "Enter a valid number"
      });
      return;
    }

    // Validate max > min
    if (max < min) {
      setError("jp_max_exp", {
        type: "manual",
        message: "Max exp must be greater than or equal to min exp"
      });
      return;
    }

    // Validate max <= 99
    if (max > 99) {
      setError("jp_max_exp", {
        type: "manual",
        message: "Max exp cannot be greater than 99"
      });
      return;
    }
  }, [clearErrors, jp_max_exp, jp_min_exp, setError]);

  useEffect(() => {
    const hasMinExperience = jp_min_exp !== undefined && jp_min_exp !== "";
    const hasMaxExperience = jp_max_exp !== undefined && jp_max_exp !== "";

    if (hasMinExperience && hasMaxExperience) {
      validateExperience();
    }
  }, [jp_min_exp, jp_max_exp, validateExperience]);

  const handleMaxExpBlur = () => {
    validateExperience();
  };

  const handleMinExpBlur = () => {
    validateExperience();
  };

  // Add new skills
  const { mutate: addSkill, isPending: isAdding } = useAddSkills();

  // Add new benefits
  const { mutate: addBenefits, data: newBenefits } = useAddBenefits();

  // Get Benefit list
  const { data: benefits } = useGetBenefits({ keys: [newBenefits] });

  // Generate job description using AI
  const { mutate: generateJD, isPending: isGeneratingJD } = useGenerateJd({
    onSuccess: (data) => {
      const generatedDescription =
        typeof data === "string"
          ? data
          : data?.jobDescription ||
            data?.description ||
            data?.data?.jobDescription ||
            data?.data?.description;

      if (!generatedDescription) {
        setDescriptionError({ error: true, message: "AI did not return a job description" });
        return;
      }

      setJobDescription(generatedDescription);
      setDescriptionError({ error: false, message: "" });
      setEditorVersion((version) => version + 1);
      setHasGeneratedDescription(true);
      setShowJDWarning(true);
    },
    onError: (error) => {
      const status = error?.status;
      if (status === 429) {
        setDescriptionError({
          error: true,
          message: "You've reached the request limit. Please try again later."
        });
      } else {
        setDescriptionError({ error: true, message: "Unable to generate a job description" });
      }
    }
  });

  const handleGenerateJobDescription = () => {
    const values = getValues();
    const getLabel = (value) => value?.label || value?.mainText || value || "";
    const getLabels = (value) =>
      Array.isArray(value) ? value.map(getLabel).filter(Boolean).join(", ") : getLabel(value);

    setDescriptionError({ error: false, message: "" });

    const payload = {
      jobTitle: values.jp_title || ""
    };

    if (values.jp_industry) payload.industry = getLabel(values.jp_industry);
    if (values.jp_min_exp !== undefined && values.jp_max_exp !== undefined) {
      payload.experienceLevel = `${values.jp_min_exp || 0}-${values.jp_max_exp || 0} years`;
    }
    const location = [values.jp_location, values.jp_city, values.jp_state, values.jp_country]
      .filter(Boolean)
      .map(getLabels)
      .filter(Boolean)
      .join(", ");

    if (location) {
      payload.location = location;
    }
    if (values.jp_type) payload.employmentType = getLabel(values.jp_type);
    if (values.jp_required_skills) payload.skills = getLabels(values.jp_required_skills);
    if (values.jp_qualification || values.jp_required_degree) {
      payload.qualifications = [getLabel(values.jp_qualification), values.jp_required_degree]
        .filter(Boolean)
        .join(", ");
    }

    generateJD(payload);
  };

  const getEmptyRequiredJDGenerationFields = () => {
    const values = getValues();
    const emptyFields = [];
    if (!values.jp_title) emptyFields.push("Job Title");
    if (!values.jp_required_skills?.length) emptyFields.push("Required Skills");
    if (!values.jp_type) emptyFields.push("Employment Type");
    if (!values.jp_location?.length) emptyFields.push("Location Type");
    if (!values.jp_city?.length) emptyFields.push("City");
    if (!values.jp_qualification) emptyFields.push("Qualification");
    if (!values.jp_required_degree) emptyFields.push("Required Degree");
    if (values.jp_min_exp === undefined || values.jp_min_exp === null || values.jp_min_exp === "") {
      emptyFields.push("Minimum Experience");
    }
    if (values.jp_max_exp === undefined || values.jp_max_exp === null || values.jp_max_exp === "") {
      emptyFields.push("Maximum Experience");
    }
    return emptyFields;
  };

  const confirmJobDescriptionOverwrite = () => {
    const emptyFields = getEmptyRequiredJDGenerationFields();

    if (emptyFields.length > 0) {
      setMissingJDFields(emptyFields);
      setShowMissingJDFieldsDialog(true);
      return;
    }

    if (!isJobDescriptionEmpty(jobDescription)) {
      setShowConfirmOverwriteDialog(true);
      return;
    }

    handleGenerateJobDescription();
  };

  const handleConfirmJobDescriptionOverwrite = () => {
    setShowConfirmOverwriteDialog(false);
    handleGenerateJobDescription();
  };

  const { mutate: sendTestEmail, isPending: isSendingTestEmail } = useSendTestEmailNotification({
    onSuccess: (data) => {
      setEmailSent(true);
      setDisableEmailButton(true);
      setTimeout(() => {
        setEmailSent(false);
        setDisableEmailButton(false);
      }, 10000);
    },
    onError: (error) => {
      console.error("Error sending test email:", error);
    }
  });

  const savedBenefits = preloadedData
    ? preloadedData.Benefits
      ? preloadedData.Benefits.map(({ jp_id, jp_benefit }) => ({
          jp_id,
          label: jp_benefit
        }))
      : []
    : [];

  const onSubmitData = (data) => {
    // this means react quill editor is empty
    if (isJobDescriptionEmpty(jobDescription)) {
      setDescriptionError({ error: true, message: "Job description is required" });
      return;
    }
    const JobPost = {
      ...data,
      jp_location: convertListToString(data.jp_location),
      jp_benefits: convertListToString(data.jp_benefits),
      jp_required_skills: convertListToString(data.jp_required_skills),
      jp_keywords: data.jp_keywords?.join("| ") || "",
      jp_start_date: data.jp_start_date.toISOString().split("T")[0],
      jp_end_date: data.jp_end_date.toISOString().split("T")[0],
      jp_city: Array.isArray(data.jp_city) ? data.jp_city.join("| ") : data.jp_city,
      jp_state: Array.isArray(data.jp_state) ? convertListToString(data.jp_state) : data.jp_state,
      jp_country: Array.isArray(data.jp_country)
        ? convertListToString(data.jp_country)
        : data.jp_country,
      jp_is_visible: false,
      jp_qualification: data.jp_qualification.label,
      jp_type: data.jp_type.label,
      jp_started_at: getCurrentTimeStamp(),
      jp_company_id: isAdminRole ? data.jp_company_name.id : user.id,
      jp_company_name: isAdminRole ? data.jp_company_name.label : user.name,
      jp_description: jobDescription,
      jp_role: user.role
    };

    if (location.pathname.includes("/jobs/listings")) {
      JobPost.jp_updated_at = getCurrentTimeStamp();
      JobPost.jp_updated_by = user.id;
    }

    if (location.pathname.includes("/jobs/postjob")) {
      JobPost.jp_created_at = getCurrentTimeStamp();
      JobPost.jp_created_by = user.id;
    }

    mutationFn({ data: JobPost });
  };

  const sendTestEmailNotification = () =>
    sendTestEmail({ jobId: preloadedData.jp_id, toEmail: user.email, updatedBy: user.id });

  return (
    <Fragment>
      <AddNewSkillDialog
        open={openSkillDialog}
        onClose={() => setOpenSkillDialog(false)}
        addFn={addSkill}
        loading={isAdding}
      />
      <MissingJDFieldsDialog
        open={showMissingJDFieldsDialog}
        onClose={() => setShowMissingJDFieldsDialog(false)}
        fields={missingJDFields}
      />
      <AddNewBenefitsDialog
        open={openBenefitDialog}
        onClose={() => setOpenBenefitDialog(false)}
        addFn={addBenefits}
        loading={false}
      />
      <ConfirmOverwriteDialog
        open={showConfirmOverwriteDialog}
        onClose={() => setShowConfirmOverwriteDialog(false)}
        onConfirm={handleConfirmJobDescriptionOverwrite}
      />
      <ContentBox>
        <h1>{title}</h1>
        <form onSubmit={handleSubmit(onSubmitData)}>
          <Grid item lg={6} md={6} sm={12} xs={12} sx={{ mt: 2 }}>
            {/* Job Title */}
            <AddTitleAndCompany
              control={control}
              preloadedData={preloadedData}
              filterOptions={filterOptions}
            />
            <DatesAndExperience
              control={control}
              isEditPath={isEditPath}
              preloadedData={preloadedData}
              dateToday={dateToday}
              startDate={startDate}
              endDate={endDate}
              errors={errors}
              clearErrors={clearErrors}
              handleMaxExpBlur={handleMaxExpBlur}
              handleMinExpBlur={handleMinExpBlur}
            />
            {/* Add Qualification Requirements */}
            <h3>Qualification Requirements</h3>
            <AddQualificationRequirement control={control} preloadedData={preloadedData} />
            <SkillsAndSalary
              control={control}
              preloadedData={preloadedData}
              filterOptions={filterOptions}
              skills={skills}
              setOpenSkillDialog={setOpenSkillDialog}
              setError={setError}
              clearErrors={clearErrors}
              minimumSalary={minimumSalary}
              maximumSalary={maximumSalary}
            />
            {/* Location */}
            <h3>Location</h3>
            <MultiSelectLocation
              control={control}
              preloadedData={preloadedData}
              setValue={setValue}
              watch={watch}
            />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {/* Benefits */}
                <AddBenefits
                  control={control}
                  setOpenBenefitDialog={setOpenBenefitDialog}
                  savedBenefits={savedBenefits}
                  benefits={benefits}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {/* Add Keywords */}
                <AddKeywords control={control} preloadedData={preloadedData} />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <h3>Provide a Job Description</h3>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <GenerateJobDescriptionButton
                  label={hasGeneratedDescription ? "Regenerate With AI" : "Generate With AI"}
                  loading={isGeneratingJD}
                  onGenerate={confirmJobDescriptionOverwrite}
                />
                {descriptionError.error && (
                  <Typography color={"error"}>{descriptionError.message}</Typography>
                )}
              </Box>
            </Box>
            <Box mb={2}>
              {showJDWarning && (
                <Typography color={"warning.main"}>
                  * Please review the generated job description and make any necessary edits before
                  submitting.
                </Typography>
              )}
              <ReactQuillEditor
                key={editorVersion}
                defaultValue={jobDescription || ""}
                onTextChange={(text) => setJobDescription(text)}
              />
            </Box>
          </Grid>
          <Box>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 2
              }}
            >
              <SubmitJobButton
                disabled={isFieldError || disableSubmit || isGeneratingJD}
                isEdit={isEdit}
                loading={loadingState}
              />
              {showSendEmailButton && (
                <SendTestEmailButton
                  loading={isSendingTestEmail}
                  disabled={disableEmailButton}
                  emailSent={emailSent}
                  onSend={sendTestEmailNotification}
                />
              )}
              {isEmailVerified && isAdminRole ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "success.main",
                    gap: 1
                  }}
                >
                  <Icon>verified</Icon>
                  <Span sx={{ textTransform: "capitalize", fontWeight: "bold" }}>
                    Email Verified
                  </Span>
                </Box>
              ) : (
                <></>
              )}
            </Box>
            {isFieldError && <Typography color={"error"}>Please recheck your inputs</Typography>}
          </Box>
        </form>
      </ContentBox>
    </Fragment>
  );
}

export default PostJobForm;
